import { FormContainer, useConfirmation } from '@wyz/ui';
import { invariant, useModalState } from '@wyz/utils';
import { pick } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { useManageQuoteCart, useManageSearch } from '~/purchase/api';
import {
  QuoteDetailModal,
  QuoteRowItem,
  QuoteStandardItem,
} from '~/purchase/components';
import { QuoteHelpers } from '~/purchase/helpers';
import { useQuoteCart } from '~/purchase/hooks';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IQuoteAddToCartForm, IQuoteTyreOffer } from '~/purchase/types';

type Props = {
  tyreOffer: IQuoteTyreOffer;
  showBuyingInfo?: boolean;
  defaultQuantity: number;
  customer: IUserCustomer;
  showBfDiscount: boolean;
  onRefreshTyre: (ean: string, quantity: number) => void;
  type?: 'CARD' | 'LINE';
};

function ProductResult({
  tyreOffer,
  defaultQuantity,
  showBuyingInfo = false,
  customer,
  showBfDiscount,
  onRefreshTyre,
  type = 'CARD',
}: Props) {
  const { t } = useI18n();
  const ask = useConfirmation();
  const methods = useForm<IQuoteAddToCartForm>({
    defaultValues: { quantity: defaultQuantity },
  });
  const { actions } = useQuoteCart();
  const manageCart = useManageQuoteCart({
    get: { enabled: true },
  });

  const [modal, modalHandlers] = useModalState<'DETAIL'>();
  const quoteItemInfo = QuoteHelpers.getItemInfoFromTyreOffer(tyreOffer);

  const handleAddToCart = (quantity: number) => {
    invariant(manageCart.get.isSuccess);
    const cart = manageCart.get.data.data;
    if (
      cart.dimensionCount === QuoteHelpers.QUOTE_CART_MAX_DIMENSION_COUNT ||
      cart.referenceCount >= QuoteHelpers.QUOTE_CART_MAX_REFERENCE_COUNT ||
      (cart.referenceCount > 1 &&
        !cart.items.some((item) =>
          QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
        ))
    ) {
      ask({
        title: t('app.purchase.quote_cart_page.messages.cannot_add_dimension'),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.common.button.cancel'),
      }).onConfirm(() => actions.reset.process());
      return;
    } else if (
      cart.dimensionCount === 1 &&
      !cart.items.some((item) =>
        QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
      )
    ) {
      ask({
        title: t(
          'app.purchase.quote_cart_page.messages.different_dimension.title',
        ),
        description: t(
          'app.purchase.quote_cart_page.messages.different_dimension.description',
        ),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.purchase.quote_search_page.add_to_quote'),
      })
        .onConfirm(() => actions.reset.process())
        .onCancel(() => {
          invariant(quoteItemInfo.pricing);
          const payload = {
            ...pick(quoteItemInfo, ['tyre', 'stocks']),
            buyingPrice: quoteItemInfo.pricing.buyingPrice,
            unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
            quantity,
            currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
          };
          actions.addItem.process(payload);
        });
    } else {
      invariant(quoteItemInfo.pricing);
      const payload = {
        ...pick(quoteItemInfo, ['tyre', 'stocks', 'proximityStocks']),
        buyingPrice: quoteItemInfo.pricing.buyingPrice,
        unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
        recyclingFee: quoteItemInfo.pricing.recyclingFee,
        quantity,
        currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
      };
      actions.addItem.process(payload);
    }
  };

  const searchContext = {
    customerId: customer.id,
    searchType: SEARCH_TYPE_ENUM.QUOTE,
  };

  const manageSearch = useManageSearch<IQuoteTyreOffer>({
    context: searchContext,
    refreshEan: { enabled: true },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={({ quantity }) => {
        handleAddToCart(quantity);
      }}
      className='position-relative'
    >
      {type === 'CARD' ? (
        <QuoteStandardItem
          quoteItemInfo={quoteItemInfo}
          customer={customer}
          showBfDiscount={showBfDiscount}
          showBuyingInfo={showBuyingInfo}
          tyreOffer={tyreOffer}
          onRefreshTyre={onRefreshTyre}
          onShowDetails={() => modalHandlers.open('DETAIL')}
          addItemLoading={actions.addItem.isLoading}
        />
      ) : (
        <QuoteRowItem
          quoteItemInfo={quoteItemInfo}
          tyreOffer={tyreOffer}
          onShowDetails={() => modalHandlers.open('DETAIL')}
          showBuyingInfo={showBuyingInfo}
          addItemLoading={actions.addItem.isLoading}
          onRefreshTyre={onRefreshTyre}
          customer={customer}
        />
      )}
      {modal === 'DETAIL' && (
        <QuoteDetailModal
          customer={customer}
          item={quoteItemInfo}
          onClose={() => modalHandlers.close()}
          currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
        />
      )}
      {manageSearch.refreshEan.isLoading && <div className='shimmer-overlay' />}
    </FormContainer>
  );
}
export default ProductResult;
